import React from 'react';
import { Button, Flex, spacing, useTheme } from '@fti/design-system';

interface DataGridToolbarProps {
  hasDraftChanges: boolean;
  canUndo: boolean;
  canRedo: boolean;
  selectedRowCount: number;
  selectedColumnCount: number;
  isSaving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onAddRow: () => void;
  onDeleteRows: () => void;
  onAddColumn: () => void;
  onDeleteColumns: () => void;
  rightContent?: React.ReactNode;
}

export const DataGridToolbar: React.FC<DataGridToolbarProps> = ({
  hasDraftChanges,
  canUndo,
  canRedo,
  selectedRowCount,
  selectedColumnCount,
  isSaving,
  onSave,
  onDiscard,
  onUndo,
  onRedo,
  onAddRow,
  onDeleteRows,
  onAddColumn,
  onDeleteColumns,
  rightContent,
}) => {
  const { theme } = useTheme();

  return (
    <Flex justify="between" align="center" style={{ padding: `${spacing.sm} 0` }}>
      <Flex gap="sm" align="center">
        <Button variant="outline" size="sm" onClick={onAddRow}>
          + Row
        </Button>
        <Button variant="outline" size="sm" onClick={onAddColumn}>
          + Column
        </Button>

        {selectedRowCount > 0 && (
          <Button variant="outline" color="error" size="sm" onClick={onDeleteRows}>
            Delete {selectedRowCount} Row{selectedRowCount > 1 ? 's' : ''}
          </Button>
        )}

        {selectedColumnCount > 0 && (
          <Button variant="outline" color="error" size="sm" onClick={onDeleteColumns}>
            Delete {selectedColumnCount} Column{selectedColumnCount > 1 ? 's' : ''}
          </Button>
        )}

        <div style={{ width: '1px', height: '24px', backgroundColor: theme.colors.border.default, margin: `0 ${spacing.xs}` }} />

        <Button variant="ghost" size="sm" onClick={onUndo} disabled={!canUndo}>
          Undo
        </Button>
        <Button variant="ghost" size="sm" onClick={onRedo} disabled={!canRedo}>
          Redo
        </Button>
      </Flex>

      <Flex gap="sm" align="center">
        {hasDraftChanges && (
          <>
            <span style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
              Unsaved changes
            </span>
            <Button variant="outline" color="neutral" size="sm" onClick={onDiscard}>
              Discard All
            </Button>
            <Button variant="solid" color="primary" size="sm" onClick={onSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </>
        )}
        {rightContent}
      </Flex>
    </Flex>
  );
};
